import React, { ReactElement, useRef } from 'react';
import { SnackbarProviderProps, SnackbarProvider as SnackStackProvider } from 'notistack';
import { createStyles, darken, lighten, makeStyles, Theme } from '@fluentsms/components';
import { CheckCircleOutlineOutlined, ErrorOutlineOutlined, ReportProblemOutlined } from '@material-ui/icons';
import { SnackbarCloseButton } from './snackbarCloseButton';

// NOTE: We have to add !important to the styling otherwise notistack reverts it with their own
//       !important styling. This is a known issue: https://github.com/iamhosseindhv/notistack/issues/305
// TODO: Figure out how to use alert as the passed in component, but using notistack props. So we can remove the darken & lighten functions.
//       Created an issue for this on the repo: https://github.com/iamhosseindhv/notistack/issues/361
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& div:first-child': {
        flex: 1,
      },
      boxSizing: 'content-box',
    },
    icon: {
      marginRight: theme.spacing(1.25),
      alignSelf: 'flex-start',
    },
    success: {
      backgroundColor: `${lighten(theme.palette.success.light, 0.7)} !important`,
      color: `${darken(theme.palette.success.dark, 0.5)} !important`,
      '& $icon': { color: `${theme.palette.success.main} !important` },
      '& $dismiss': { color: `${darken(theme.palette.success.dark, 0.6)} !important` },
      width: theme.spacing(67.5),
      maxWidth: theme.spacing(67.5),
      minHeight: theme.spacing(5),
    },
    error: {
      backgroundColor: `${lighten(theme.palette.error.light, 0.7)} !important`,
      color: `${darken(theme.palette.error.dark, 0.5)} !important`,
      '& $icon': { color: `${theme.palette.error.main} !important` },
      '& $dismiss': { color: `${darken(theme.palette.error.dark, 0.6)} !important` },
      width: theme.spacing(67.5),
      maxWidth: theme.spacing(67.5),
      minHeight: theme.spacing(5),
    },
    warning: {
      backgroundColor: `${lighten(theme.palette.warning.light, 0.7)} !important`,
      color: `${darken(theme.palette.warning.dark, 0.5)} !important`,
      '& $icon': { color: `${theme.palette.warning.main} !important` },
      '& $dismiss': { color: `${darken(theme.palette.warning.dark, 0.6)} !important` },
      width: theme.spacing(67.5),
      maxWidth: theme.spacing(67.5),
      minHeight: theme.spacing(5),
    },
    info: {
      backgroundColor: `${lighten(theme.palette.info.light, 0.7)} !important`,
      color: `${darken(theme.palette.info.dark, 0.5)} !important`,
      '& $icon': { color: `${theme.palette.info.main} !important` },
      '& $dismiss': { color: `${darken(theme.palette.info.dark, 0.6)} !important` },
      width: theme.spacing(67.5),
      maxWidth: theme.spacing(67.5),
      minHeight: theme.spacing(5),
    },
  }),
);

export default function SnackbarProvider({ children, action, ...props }: SnackbarProviderProps): ReactElement {
  const classes = useStyles();

  const ref = useRef<SnackStackProvider>(null);

  return (
    <SnackStackProvider
      data-testid="snackbar"
      maxSnack={4}
      dense
      autoHideDuration={5000}
      anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      preventDuplicate
      iconVariant={{
        default: <ErrorOutlineOutlined className={classes.icon} />,
        success: <CheckCircleOutlineOutlined className={classes.icon} />,
        error: <ErrorOutlineOutlined className={classes.icon} />,
        warning: <ReportProblemOutlined className={classes.icon} />,
        info: <ErrorOutlineOutlined className={classes.icon} />,
      }}
      classes={{
        variantSuccess: classes.success,
        variantError: classes.error,
        variantWarning: classes.warning,
        variantInfo: classes.info,
      }}
      className={classes.root}
      ref={ref}
      action={(key) => {
        const dismissAction = action ? (
          action
        ) : (
          <SnackbarCloseButton
            snackbarId={key}
            onClick={(onClickKey) => {
              ref?.current?.closeSnackbar(onClickKey);
            }}
          />
        );

        return dismissAction;
      }}
      {...props}
    >
      {children}
    </SnackStackProvider>
  );
}
